import { useEffect, useMemo, useState } from 'react';
import { fetchAgendaTimeline } from '../../api/agenda.api';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const startPadding = (firstDay.getDay() + 6) % 7;
  for (let i = startPadding - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }

  return days;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function CommunicationCalendar({ onOpenCampaign }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().slice(0, 10);
      const endDate = new Date(year, month + 1, 0).toISOString().slice(0, 10);
      try {
        const data = await fetchAgendaTimeline(startDate, endDate, 'COMMUNICATION', ['COMMUNICATION']);
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        setEvents([]);
        setError('Impossible de charger le calendrier des campagnes.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentDate]);

  const monthDays = useMemo(
    () => getMonthDays(currentDate.getFullYear(), currentDate.getMonth()),
    [currentDate]
  );

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const date = e.start_time?.slice(0, 10);
      if (!date) return;
      if (!map[date]) map[date] = [];
      map[date].push(e);
    });
    return map;
  }, [events]);

  const monthName = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  function prevMonth() {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  }
  function goToday() {
    setCurrentDate(new Date());
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50">
            <ChevronLeft className="h-4 w-4 text-slate-700" />
          </button>
          <div className="min-w-[160px] text-center text-sm font-semibold text-slate-900 capitalize">{monthName}</div>
          <button onClick={nextMonth} className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50">
            <ChevronRight className="h-4 w-4 text-slate-700" />
          </button>
          <button onClick={goToday} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
            Aujourd'hui
          </button>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
          <CalendarDays className="h-4 w-4" />
          Campagnes planifiées
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">Chargement…</div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-slate-500 py-2">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map(({ date, isCurrentMonth }, idx) => {
              const dateStr = date.toISOString().slice(0, 10);
              const dayEvents = eventsByDate[dateStr] || [];
              const isToday = dateStr === new Date().toISOString().slice(0, 10);

              return (
                <div
                  key={idx}
                  className={`min-h-[96px] rounded-xl border p-2 ${
                    isCurrentMonth ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'
                  } ${isToday ? 'ring-2 ring-slate-900' : ''}`}
                >
                  <div className={`text-xs font-medium ${isCurrentMonth ? 'text-slate-900' : 'text-slate-400'}`}>
                    {date.getDate()}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => onOpenCampaign?.(ev.source_id)}
                        className="w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 truncate"
                        title={ev.title}
                      >
                        {formatTime(ev.start_time)} • {ev.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-slate-500 px-1">+{dayEvents.length - 3} autres</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
